import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { getEventTypeStyle, getEstadoStyle } from './AdvancedFilters';

const SORT_KEYS = ['fecha', 'cliente', 'tipoServicio', 'empleadoId', 'estado', 'precioServicio', 'numPiezas'];

const ListaEventos = ({ eventos, empleados, onEventClick, onEdit, onDelete }) => {
  const [sortKey, setSortKey] = useState('fecha');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState('');

  const getEmpleadoNombre = (id) => empleados?.find(e => e?.id === id)?.nombre || '-';

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const filtered = (eventos || [])?.filter(ev => {
    if (!search) return true;
    const s = search?.toLowerCase();
    return (ev?.cliente || '')?.toLowerCase()?.includes(s) ||
      (ev?.tipoServicio || '')?.toLowerCase()?.includes(s) ||
      (ev?.estado || '')?.toLowerCase()?.includes(s) ||
      (ev?.ubicacion || '')?.toLowerCase()?.includes(s) ||
      getEmpleadoNombre(ev?.empleadoId)?.toLowerCase()?.includes(s);
  });

  const sorted = [...filtered]?.sort((a, b) => {
    const av = a?.[sortKey] ?? '';
    const bv = b?.[sortKey] ?? '';
    if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av)?.localeCompare(String(bv)) : String(bv)?.localeCompare(String(av));
  });

  const total = sorted?.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const start = (page - 1) * perPage;
  const paginated = sorted?.slice(start, start + perPage);

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <Icon name="ChevronsUpDown" size={11} className="opacity-30" />;
    return <Icon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={11} style={{ color: '#D6EF4E' }} />;
  };

  const cols = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'horaInicio', label: 'Hora' },
    { key: 'cliente', label: 'Cliente' },
    { key: 'tipoServicio', label: 'Tipo' },
    { key: 'empleadoId', label: 'Empleado' },
    { key: 'estado', label: 'Estado' },
    { key: 'precioServicio', label: 'Importe' },
    { key: 'numPiezas', label: 'Kg' },
    { key: 'acciones', label: 'Acciones' },
  ];

  return (
    <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
      {/* Search + per page */}
      <div className="p-4 border-b border-white/8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" placeholder="Buscar en lista..." value={search} onChange={e => { setSearch(e?.target?.value); setPage(1); }}
            className="w-full bg-[#111] border border-white/10 rounded-lg pl-8 pr-3 py-2 text-xs font-oranienbaum text-white placeholder-white/30 focus:outline-none focus:border-[#D6EF4E]/50" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/40 font-oranienbaum">Mostrar:</span>
          <select value={perPage} onChange={e => { setPerPage(Number(e?.target?.value)); setPage(1); }}
            className="bg-[#111] border border-white/10 rounded-lg px-2 py-2 text-xs font-oranienbaum text-white focus:outline-none focus:border-[#D6EF4E]/50">
            {[25, 50, 100, 200]?.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <span className="text-xs text-white/30 font-oranienbaum">
          Mostrando {total === 0 ? 0 : start + 1}–{Math.min(start + perPage, total)} de {total}
        </span>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/8">
              {cols?.map(col => (
                <th key={col?.key}
                  onClick={() => col?.key !== 'acciones' && handleSort(col?.key)}
                  className={`px-4 py-3 text-left text-xs font-oranienbaum font-semibold text-white/50 uppercase tracking-wide whitespace-nowrap ${col?.key !== 'acciones' ? 'cursor-pointer hover:text-white/80' : ''}`}>
                  <span className="flex items-center gap-1">
                    {col?.label}
                    {col?.key !== 'acciones' && <SortIcon k={col?.key} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {paginated?.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-white/30 font-oranienbaum text-sm">Sin resultados</td></tr>
            ) : paginated?.map(ev => {
              const typeStyle = getEventTypeStyle(ev?.tipoServicio);
              const estadoStyle = getEstadoStyle(ev?.estado);
              return (
                <tr key={ev?.id} className="hover:bg-white/3 transition-colors cursor-pointer group" onClick={() => onEventClick(ev)}>
                  <td className="px-4 py-3 text-xs font-oranienbaum text-white/70 whitespace-nowrap">{ev?.fecha || '-'}</td>
                  <td className="px-4 py-3 text-xs font-oranienbaum text-white/50 whitespace-nowrap">{ev?.horaInicio || '-'}</td>
                  <td className="px-4 py-3 text-sm font-oranienbaum font-semibold text-white group-hover:text-[#D6EF4E] transition-colors whitespace-nowrap">{ev?.cliente || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-xs font-oranienbaum px-2 py-0.5 rounded-full border" style={{ color: typeStyle?.color, borderColor: `${typeStyle?.color}40`, background: typeStyle?.bg }}>
                      {ev?.tipoServicio || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-oranienbaum text-white/60 whitespace-nowrap">{getEmpleadoNombre(ev?.empleadoId)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-xs font-oranienbaum px-2 py-0.5 rounded-full border" style={{ color: estadoStyle?.color, borderColor: `${estadoStyle?.color}40`, background: estadoStyle?.bg }}>
                      {ev?.estado || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-oranienbaum font-semibold whitespace-nowrap" style={{ color: ev?.precioServicio ? '#D6EF4E' : '#ffffff30' }}>
                    {ev?.precioServicio ? `${parseFloat(ev?.precioServicio)?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €` : '-'}
                  </td>
                  <td className="px-4 py-3 text-xs font-oranienbaum text-white/50 whitespace-nowrap">{ev?.numPiezas ? `${ev?.numPiezas} kg` : '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap" onClick={e => e?.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button onClick={() => onEventClick(ev)} className="p-1.5 rounded hover:bg-white/10 transition-colors" title="Ver">
                        <Icon name="Eye" size={13} style={{ color: '#D6EF4E' }} />
                      </button>
                      <button onClick={() => onEdit(ev)} className="p-1.5 rounded hover:bg-white/10 transition-colors" title="Editar">
                        <Icon name="Edit" size={13} style={{ color: '#3A7F6B' }} />
                      </button>
                      <button onClick={() => onDelete(ev?.id)} className="p-1.5 rounded hover:bg-white/10 transition-colors" title="Eliminar">
                        <Icon name="Trash2" size={13} style={{ color: '#ef4444' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-white/8 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={page === 1} className="px-2 py-1.5 text-xs font-oranienbaum rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">«</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-2 py-1.5 text-xs font-oranienbaum rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">‹</button>
            <span className="px-3 py-1.5 text-xs font-oranienbaum text-white/50">Pág. {page} de {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-2 py-1.5 text-xs font-oranienbaum rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">›</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-2 py-1.5 text-xs font-oranienbaum rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">»</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaEventos;
